// The daily expiry list (plan §9.1; D-049): at the start of each trading day, per branch,
// which lots change expiry state today and **why** (the rule and the best-before in words),
// what is done, still to do or skipped, moves back to an earlier state flagged for a person,
// and the list's coverage ("checked 412 of 412 expiry-managed lots with stock"). "Make
// today's list now" runs the morning job for a branch (to test, or when it didn't run).

import type { StateTask, StateTasksResponse } from '@enp/domain/api';
import { STATE_TASK_STATUSES } from '@enp/domain/api';
import {
  Badge,
  Button,
  cn,
  createColumnHelper,
  DataTable,
  DateField,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  toast,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  CalendarCheck,
  CircleAlert,
  History,
  ListChecks,
  RefreshCw,
  SearchX,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { useMemo } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { stateLabel } from '../../../catalogue/labels.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { Notice } from '../../../stations/page-parts.js';
import { actionProblem, plural, useHasPermission } from '../../../stock-control/parts.js';
import {
  expirySearchSchema,
  type ExpirySearch,
  generateStateList,
  invalidateStockControl,
  stateTasksQuery,
} from '../../../stock-control/queries.js';
import { coverageText, qtyWithUnit, TASK_STATUS } from '../../../stock-control/words.js';
import { FilterSelect, StockStateBadge, StockViews } from '../../../stock/components.js';
import { plainDateLabel, accountToday } from '../../../stock/labels.js';
import { clean } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/stock/expiry')({
  validateSearch: expirySearchSchema,
  component: ExpiryPage,
});

const TABLE_LABEL = 'State changes';

type Summary = StateTasksResponse['branches'][number];

/**
 * Where the task takes the stock: its state, or "Written off" when the state is off sale for
 * good, as the stations do it (UI4S-205); moving it to that state is the other choice, which
 * the Why says (UI8X-2).
 */
function TaskTarget({ task }: { task: StateTask }) {
  return task.write_off ? (
    <Badge tone="danger">
      <Trash2 aria-hidden />
      Written off
    </Badge>
  ) : (
    <StockStateBadge state={task.to_state} />
  );
}

function taskColumns(today: string, multiBranch: boolean, branchName: (id: string) => string) {
  const col = createColumnHelper<StateTask>();
  const cols: ColumnDef<StateTask, unknown>[] = [
    col.display({
      id: 'product',
      header: 'Product and lot',
      meta: { width: 'min-w-52' },
      cell: ({ row }) => {
        const t = row.original;
        return (
          <span className="flex flex-col gap-0.5 leading-tight">
            <Link
              to="/stock/$productId"
              params={{ productId: t.product.id }}
              className="font-medium text-fg hover:underline"
            >
              {t.product.name}
            </Link>
            <span className="text-xs text-muted">
              <span className="font-mono">{t.lot.lot_code}</span> · {t.location.name}
              {multiBranch && ` · ${branchName(t.branch_id)}`}
            </span>
            {/* Below xl the move sits here, so no column is cut off on a tablet (UI4A-213). */}
            <span className="mt-1 flex items-center gap-1.5 whitespace-nowrap xl:hidden">
              <StockStateBadge state={t.from_state} />
              <ArrowRight aria-label="to" className="size-4 text-muted" />
              <TaskTarget task={t} />
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'move',
      header: 'Move',
      meta: { width: 'min-w-56', className: 'hidden xl:table-cell' },
      cell: ({ row }) => {
        const t = row.original;
        return (
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <StockStateBadge state={t.from_state} />
            <ArrowRight aria-label="to" className="size-4 text-muted" />
            <TaskTarget task={t} />
          </span>
        );
      },
    }),
    col.display({
      id: 'qty',
      // Its own column, with the unit (UI4A-6); the lot's quantity when the list was made,
      // not necessarily what was moved (UI4A-217).
      header: 'Qty at list time',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums text-fg">
          {qtyWithUnit(row.original.qty, row.original.product.base_uom)}
        </span>
      ),
    }),
    col.display({
      id: 'why',
      header: 'Why',
      meta: { width: 'min-w-64' },
      cell: ({ row }) => {
        const t = row.original;
        return (
          <span className="flex flex-col gap-1 leading-snug">
            <span className="text-fg">{t.reason}</span>
            {t.write_off && (
              <span className="text-sm text-fg">
                Or moved to {stateLabel(t.to_state)} at the station: kept on the books, off sale.
              </span>
            )}
            <span className="text-xs text-muted">“{t.rule_name}” rule</span>
            <span className="flex flex-wrap gap-1">
              {t.kind === 'backward' && (
                <Badge tone="warning">
                  <Undo2 aria-hidden />
                  Back to an earlier state: a manager decides
                </Badge>
              )}
              {t.overdue && (
                // The day it fell due is in the Why text (the rule's date); the badge
                // doesn't restate it with another date (UI4A-7).
                <Badge tone="danger">
                  <CircleAlert aria-hidden />
                  Overdue
                </Badge>
              )}
              {t.first_due_on < t.due_on && (
                <Badge tone="neutral">
                  <History aria-hidden />
                  {/* Came from an earlier day; "Carried to next day" is a task's own status. */}
                  Carried over from {plainDateLabel(t.first_due_on, today)}
                </Badge>
              )}
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const t = row.original;
        const s = TASK_STATUS[t.status];
        return (
          <span className="flex flex-col items-start gap-1">
            <StatusBadge status={s.status}>{s.label}</StatusBadge>
            {t.closed_at && (
              <span className="text-xs text-muted">
                {t.closed_by?.name ?? 'Someone'} · <AccountDate value={t.closed_at} withTime />
              </span>
            )}
            {t.close_note && (
              <span className="max-w-48 truncate text-xs text-muted" title={t.close_note}>
                “{t.close_note}”
              </span>
            )}
          </span>
        );
      },
    }),
  ];
  return cols;
}

function BranchCard({
  summary,
  isToday,
  canMake,
  onMake,
  making,
}: {
  summary: Summary;
  isToday: boolean;
  canMake: boolean;
  onMake: () => void;
  making: boolean;
}) {
  const coverage = coverageText(summary.lots_evaluated, summary.lots_with_stock);
  const total = summary.pending + summary.done + summary.skipped + summary.rolled_over;
  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="font-display text-lg font-semibold text-fg">{summary.branch.name}</h2>
          <p className="text-sm text-muted">
            {summary.generated_at ? (
              <>
                List made <AccountDate value={summary.generated_at} withTime />
              </>
            ) : isToday ? (
              'Today’s list hasn’t been made yet'
            ) : (
              'No list was made that day'
            )}
          </p>
        </div>
        {isToday && canMake && (
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw aria-hidden />}
            loading={making}
            onClick={onMake}
          >
            {summary.generated_at ? 'Top up today’s list' : 'Make today’s list now'}
          </Button>
        )}
      </div>
      {summary.generated_at && (
        <p
          className={cn(
            'flex items-center gap-1.5 text-body',
            coverage.complete ? 'text-fg' : 'font-medium text-state-warning',
          )}
        >
          {coverage.complete ? (
            <CalendarCheck aria-hidden className="size-4 text-state-success" />
          ) : (
            <CircleAlert aria-hidden className="size-4" />
          )}
          {coverage.text}
        </p>
      )}
      {summary.generated_at && (
        <dl className="grid grid-cols-4 gap-2 text-center">
          {(
            [
              // The same words as each task's status (UI4A-7).
              [
                TASK_STATUS.pending.label,
                summary.pending,
                summary.pending > 0 ? 'text-state-warning' : 'text-fg',
              ],
              [TASK_STATUS.done.label, summary.done, 'text-fg'],
              [TASK_STATUS.skipped.label, summary.skipped, 'text-fg'],
              [TASK_STATUS.rolled_over.label, summary.rolled_over, 'text-fg'],
            ] as const
          ).map(([label, n, tone]) => (
            <div key={label} className="flex flex-col rounded-md bg-surface-sunken px-2 py-2">
              <dt className="text-xs text-muted">{label}</dt>
              <dd className={cn('font-display text-xl font-semibold tabular-nums', tone)}>{n}</dd>
            </div>
          ))}
        </dl>
      )}
      {summary.generated_at && total === 0 && (
        <p className="text-sm text-muted">No lot changes state today.</p>
      )}
    </li>
  );
}

function ExpiryPage() {
  useDocumentTitle('Expiry list');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canMake = useHasPermission('stock.adjust') === true;
  const setSearch = (patch: Partial<ExpirySearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  const q = useQuery({ ...stateTasksQuery(search.day), placeholderData: keepPreviousData });
  const data = q.data;
  const day = data?.day ?? search.day ?? today;
  const isToday = day === today;

  const make = useMutation({
    mutationFn: (branchId: string) => generateStateList(branchId),
    onSuccess: async (result) => {
      await invalidateStockControl(queryClient);
      const branch = data?.branches.find((b) => b.branch.id === result.branch_id)?.branch.name;
      toast.success(
        result.created > 0
          ? `${plural(result.created, 'state change', 'state changes')} added`
          : 'The list was already complete',
        {
          description: `${branch ?? 'The branch'}: ${result.check.checked} of ${result.check.expected} lots checked${
            result.rolled_over > 0 ? `; ${result.rolled_over} carried over from earlier days` : ''
          }.`,
        },
      );
    },
    onError: (error) => {
      const p = actionProblem(error, 'The list wasn’t made');
      toast.error(p.title, { description: p.explain });
    },
  });

  const branchName = (id: string) =>
    data?.branches.find((b) => b.branch.id === id)?.branch.name ?? '';
  const multiBranch = (data?.branches.length ?? 0) > 1;
  const lotName = search.lot
    ? data?.items.find((t) => t.lot.id === search.lot)?.lot.lot_code
    : undefined;

  const items = useMemo(() => {
    const all = data?.items ?? [];
    return all.filter(
      (t) =>
        (!search.branch || t.branch_id === search.branch) &&
        (!search.status || t.status === search.status) &&
        (!search.lot || t.lot.id === search.lot) &&
        (!search.backward || t.kind === 'backward'),
    );
  }, [data, search.branch, search.status, search.lot, search.backward]);
  const backwardCount = (data?.items ?? []).filter(
    (t) => t.kind === 'backward' && t.status === 'pending',
  ).length;
  const filtered = Boolean(search.branch || search.status || search.lot || search.backward);
  const summaries = (data?.branches ?? []).filter(
    (b) => !search.branch || b.branch.id === search.branch,
  );

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No state changes match"
      description={
        search.lot
          ? 'This lot doesn’t change state on this day.'
          : 'Nothing on this day’s list fits these filters.'
      }
      action={
        <Button
          variant="secondary"
          icon={<X aria-hidden />}
          onClick={() =>
            setSearch({ branch: undefined, status: undefined, lot: undefined, backward: undefined })
          }
        >
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      tone={data && data.branches.some((b) => b.generated_at) ? 'success' : undefined}
      icon={ListChecks}
      title={
        data && data.branches.some((b) => b.generated_at)
          ? 'Nothing changes state on this day'
          : isToday
            ? 'Today’s list hasn’t been made yet'
            : 'No list for this day'
      }
      description={
        data && data.branches.some((b) => b.generated_at)
          ? 'Every lot with stock was checked against its product’s expiry rule; none is due to move.'
          : `The list is made each morning at the time in the stock-control settings. ${
              canMake && isToday ? 'Make it now to see today’s moves.' : ''
            }`
      }
      action={
        <Button variant="secondary" asChild>
          <Link to="/settings/stock-control">
            List time and approval limits
            <ArrowRight aria-hidden />
          </Link>
        </Button>
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Stock"
        description="Which lots move to their next expiry state each day, and why. Staff confirm each move at the Expiry list station."
      />
      <StockViews current="expiry" />

      <div className="mb-3">
        <h2 className="font-display text-xl font-semibold text-fg">
          Expiry list for {isToday ? 'today' : plainDateLabel(day, today)}
        </h2>
      </div>

      <div
        className="mb-4 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Expiry list filters"
      >
        <span className="flex items-center gap-2">
          <label htmlFor="expiry-day" className="text-sm text-muted">
            Day
          </label>
          <DateField
            id="expiry-day"
            value={search.day ?? today}
            today={today}
            onChange={(v) => setSearch({ day: v === today ? undefined : v })}
            wrapperClassName="w-40"
          />
        </span>
        {!isToday && (
          <Button
            variant="ghost"
            icon={<History aria-hidden />}
            onClick={() => setSearch({ day: undefined })}
          >
            Today
          </Button>
        )}
        {multiBranch && (
          <FilterSelect
            label="Branch"
            value={search.branch}
            // The branches of another day are still loading (UI4A-15).
            disabled={q.isPlaceholderData}
            allLabel="All branches"
            options={(data?.branches ?? []).map((b) => ({
              value: b.branch.id,
              label: b.branch.name,
            }))}
            onChange={(v) => setSearch({ branch: v })}
          />
        )}
        <FilterSelect
          label="Status"
          value={search.status}
          allLabel="Every status"
          options={STATE_TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS[s].label }))}
          onChange={(v) => setSearch({ status: v as ExpirySearch['status'] })}
        />
        <Button
          variant="secondary"
          aria-pressed={Boolean(search.backward)}
          className={cn(search.backward && 'border-primary/60 bg-primary-subtle text-primary-text')}
          icon={<Undo2 aria-hidden />}
          onClick={() => setSearch({ backward: search.backward ? undefined : true })}
        >
          Back to an earlier state{backwardCount > 0 ? ` (${backwardCount})` : ''}
        </Button>
        {search.lot && (
          <Badge tone="neutral" className="h-control-sm gap-1.5 pr-1">
            Lot <span className="font-mono">{lotName ?? 'chosen'}</span>
            <button
              type="button"
              aria-label="Show every lot"
              onClick={() => setSearch({ lot: undefined })}
              className="rounded-sm p-0.5 hover:bg-surface-hover"
            >
              <X aria-hidden className="size-3.5" />
            </button>
          </Badge>
        )}
      </div>

      {q.isError && !data ? (
        <ErrorPanel
          error={q.error}
          titleAs="h2"
          what="The expiry list"
          onRetry={() => void q.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-5">
          {backwardCount > 0 && !search.backward && (
            <Notice
              tone="warning"
              title={`${plural(backwardCount, 'lot goes', 'lots go')} back to an earlier state`}
            >
              Their rule now says an earlier state (a best-before was moved later). They are never
              moved by themselves: a manager checks the lot and decides at the station.
            </Notice>
          )}
          {!data ? (
            <div className="grid gap-4 lg:grid-cols-2" aria-busy="true">
              <span role="status" className="sr-only">
                Loading the expiry list…
              </span>
              {Array.from({ length: 2 }, (_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5"
                >
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-14 w-full" />
                </div>
              ))}
            </div>
          ) : summaries.length === 0 ? null : (
            <ul aria-label="Branches" className="grid gap-4 lg:grid-cols-2">
              {summaries.map((b) => (
                <BranchCard
                  key={b.branch.id}
                  summary={b}
                  isToday={isToday}
                  canMake={canMake}
                  making={make.isPending && make.variables === b.branch.id}
                  onMake={() => make.mutate(b.branch.id)}
                />
              ))}
            </ul>
          )}
          <DataTable
            label={TABLE_LABEL}
            columns={taskColumns(today, multiBranch, branchName)}
            data={items}
            loading={!data}
            empty={empty}
            filterable={false}
            getRowId={(t) => t.id}
            onRowActivate={(t) =>
              void navigate({
                to: '/stock/movements',
                search: { product: t.product.id, lot: t.lot.id },
              })
            }
            footer={
              data && items.length > 0 ? (
                <p className="text-sm text-muted">
                  {plural(items.length, 'state change', 'state changes')}. Open one to see its lot’s
                  movements.
                </p>
              ) : undefined
            }
          />
        </div>
      )}
    </>
  );
}
